import {IndicatorChartTypeEnum, LayerGroupTypeEnum, TimeScaleEnum} from "../../common/enum/enum";
import * as LZString from 'lz-string'
import {dateFromStr} from "../../common/time/TimeCalculator";
import {verifyCoordinateLonLatWgs84} from "../../common/config/config";
import {LayerConfig, MainConfig, RuntimeConfig} from "../../common/config/MainConfig";
import {prefixUrl} from "../../common/util";

/**
 * A runtime config for this extended report.
 * UI elements will change this runtime config, which will afterward override TimeComparisonConfig
 * @extends RuntimeConfig
 * @hideconstructor
 */
class ExtendedReportRuntimeConfig extends RuntimeConfig {

    getClassName(){
        return "ExtendedReportRuntimeConfig";
    }

    /**
     * Create a ExtendedReportRuntimeConfig from a serialized object
     * @param code {string} Unique identifier for the runtime config
     * @param runtimeConfigSerialization {string} serialization
     * @return {ExtendedReportRuntimeConfig}
     */
    static createFromSerialized(code, runtimeConfigSerialization) {
        let parsedConfig;
        try {
            parsedConfig = runtimeConfigSerialization
                // ? JSON.parse((runtimeConfigSerialization))
                ? JSON.parse(LZString.decompressFromBase64(runtimeConfigSerialization))
                : undefined;
            // tumbo.LOGGER.logDebug("Deserialized config: ", parsedConfig);
        } catch (e) {
            tumbo.LOGGER.logError('Error deserializing config', e);
        }
        return new ExtendedReportRuntimeConfig(code, parsedConfig);
    }

    /**
     * Clean this runtime config
     */
    clean() {
        this.config = {
            i: {},
            r: {},
            t: {}
        };
    }

    /**
     * set an indicator time scale
     * @param indCode {string}
     * @param timeScale {string}
     */
    setIndicatorTimeScale(indCode, timeScale) {
        if (!this.config.i) {
            this.config.i = {};
        }
        if (!this.config.i[indCode]) {
            this.config.i[indCode] = {};
        }
        this.config.i[indCode].ts = timeScale;
    }

    /**
     * get an indicator time scale
     * @param indCode {string}
     * @return {string}
     */
    getIndicatorTimeScale(indCode) {
        return this.config.i && this.config.i[indCode]
            ? this.config.i[indCode].ts
            : undefined;
    }
}

/**
 * The configuration of the extended report
 * @extends MainConfig
 * @hideconstructor
 */
class ExtendedReportConfig extends MainConfig {

    getClassName(){
        return "ExtendedReportConfig";
    }

    getName() {
        return this.config.report.name;
    }

    getCode() {
        return this.config.report.code;
    }

    /**
     * Current report coordinate in WGS84
     * @return {Array<number>}
     */
    getCoordLonLatWgs84() {
        return this.config.report.coordLonLatWgs84;
    }

    _getStatsPostUrlProperty() {
        return this.config.report.statsPostUrl;
    }

    _getContactPostUrlProperty() {
        return this.config.report.contactPostUrl;
    }

    getGDORDRIAValuesUrl(){
        return prefixUrl(this.config.report.URL_GDORDRIA_VALUES);
    }

    getRegionalClassGDORDRIAStatsUrl(){
        return prefixUrl(this.config.report.URL_REGIONAL_CLASSIFICATION_IN_GDORDIA_STATS);
    }

    getStatsDescUrl(){
        return prefixUrl(this.config.report.URL_STATS_DESC);
    }

    getTownAroundPointUrl(){
        return prefixUrl(this.config.report.URL_TOWN_ARROUND_POINT);
    }

    getCountryStatsUrl(){
        return prefixUrl(this.config.report.URL_COUNTRY_STATS);
    }

    getBasicStatsUrl(){
        return prefixUrl(this.config.report.URL_BASIC_STATS);
    }

    getMapImagesUrl(){
        return prefixUrl(this.config.report.URL_MAP_IMAGES);
    }

    getOtherIndicatorsValuesUrl(){
        return prefixUrl(this.config.report.URL_OTHER_INDICATORS_VALUES);
    }

    getPrecipitationIndicatorsValuesUrl(){
        return prefixUrl(this.config.report.URL_PRECIPITATION_INDICATORS_VALUES);
    }


    /**
     * Get the indicator configurations
     * @return {Array<IndicatorConfig>}
     */
    getIndicatorConfigs() {
        const indicatorConfigs = [];
        for (const lt in this.config.report.indicators) {
            const conf = this.config.report.indicators[lt];
            indicatorConfigs.push(new IndicatorConfig(conf, this.allLayerServices, this.allProducts, this.allStyles));
        }
        return indicatorConfigs;
    }

    /**
     * Override this config
     * @param runtimeConfig {ExtendedReportRuntimeConfig} The configuration override
     */
    override(runtimeConfig) {
        this.getIndicatorConfigs().forEach(element => element.override(runtimeConfig));
    };

    verify() {
        let errors = super.verify();

        if (this.getCoordLonLatWgs84()) {
            if (!verifyCoordinateLonLatWgs84(this.getCoordLonLatWgs84())) {
                errors.push('ExtendedReportConfig: invalid coordLonLatWgs84. Should be [lon, lat]');
            }
        }

        if (Object.keys(this.allProducts).length === 0)
            errors.push('ExtendedReportConfig: products is mandatory');
        else {
            for (let p in this.allProducts) {
                errors = errors.concat(this.allProducts[p].verify());
            }
        }

        if (Object.keys(this.allLayerServices).length === 0)
            errors.push('ExtendedReportConfig: services is mandatory');
        else {
            for (let p in this.allLayerServices) {
                errors = errors.concat(this.allLayerServices[p].verify());
            }
        }

        if (Object.keys(this.allStyles).length === 0)
            errors.push('ExtendedReportConfig: styles is mandatory');
        else {
            for (let p in this.allStyles) {
                errors = errors.concat(this.allStyles[p].verify());
            }
        }

        const indicatorConfigs = this.getIndicatorConfigs();
        for (const ic in indicatorConfigs) {
            errors = errors.concat(indicatorConfigs[ic].verify());
        }

        if(!this.getGDORDRIAValuesUrl())
            errors.push('ExtendedReportConfig: URL_GDORDRIA_VALUES is mandatory');

        if(!this.getRegionalClassGDORDRIAStatsUrl())
            errors.push('ExtendedReportConfig: URL_REGIONAL_CLASSIFICATION_IN_GDORDIA_STATS is mandatory');
        if(!this.getStatsDescUrl())
            errors.push('ExtendedReportConfig: URL_STATS_DESC is mandatory');

        if(!this.getTownAroundPointUrl())
            errors.push('ExtendedReportConfig: URL_TOWN_ARROUND_POINT is mandatory');
        if(!this.getCountryStatsUrl())
            errors.push('ExtendedReportConfig: URL_COUNTRY_STATS is mandatory');
        if(!this.getBasicStatsUrl())
            errors.push('ExtendedReportConfig: URL_BASIC_STATS is mandatory');
        if(!this.getMapImagesUrl())
            errors.push('ExtendedReportConfig: URL_MAP_IMAGES is mandatory');

        if(!this.getOtherIndicatorsValuesUrl())
            errors.push('ExtendedReportConfig: URL_OTHER_INDICATORS_VALUES is mandatory');
        if(!this.getPrecipitationIndicatorsValuesUrl())
            errors.push('ExtendedReportConfig: URL_PRECIPITATION_INDICATORS_VALUES is mandatory');

        return errors;
    }
}

/**
 * Layer config for the extended report
 * @extends LayerConfig
 * @hideconstructor
 */
class IndicatorConfig extends LayerConfig {

    getClassName(){
        return "IndicatorConfig";
    }

    getType() {
        return LayerGroupTypeEnum.LAYER;
    }

    /**
     * This layer's chart type
     * @return {IndicatorChartTypeEnum}
     */
    getChartType() {
        return IndicatorChartTypeEnum.getForCode(this.config.chartType);
    }

    verify() {
        let errors = [];

        if (this.config.timeScale && this.config.timeScale.length > 0
            && !TimeScaleEnum.exists(this.config.timeScale)) {
            errors.push(this.getLogId() + ": invalid timeScale. Possible values: " + TimeScaleEnum.getPossibleCodes());
        }

        if (this.config.chartType && this.config.chartType.length > 0
            && !IndicatorChartTypeEnum.exists(this.config.chartType)) {
            errors.push(this.getLogId() + ": invalid chartType. Possible values: " + IndicatorChartTypeEnum.getPossibleCodes());
        }

        if (!this.config.prodCode || this.config.prodCode.length === 0) {
            errors.push(this.getLogId() + ': No product for indicator');
        }

        const service = this.getService();
        if (!service) {
            errors.push(this.getLogId() + ': No service for code ' + this.config.srvCode);
        }

        const styleCodes = this.getStyleCodes();
        for (let s in styleCodes) {
            if (!this.allStyles[styleCodes[s]]) {
                errors.push(this.getLogId() + ': no style for code "' + styleCodes[s] + '"');
            }
        }

        return errors;
    }

    override(runtimeConfig) {
        if (runtimeConfig.getIndicatorTimeScale(this.getCode()) !== undefined) {
            this.config.timeScale = runtimeConfig.getIndicatorTimeScale(this.getCode());
        }
    }

}

export {ExtendedReportConfig, ExtendedReportRuntimeConfig}